import React, { useEffect, useRef, useId } from "react";
import {
  Button,
  CardBody,
  CardFooter,
  CardHeader,
} from "reactstrap";
import CurrencyFormat from 'src/transformer/currency-format';
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { convertPenniesToDecimals } from "../../../../utils/money";
import { Chart } from "chart.js/auto";
import millify from "millify";
import domain from 'src/config/domain';
import {
  createGradient,
  chartColors,
  richTooltipConfig,
  richAnimationConfig,
  crosshairPlugin,
} from 'src/config/chart';

import 'chartjs-adapter-moment';

// Register the crosshair plugin
Chart.register(crosshairPlugin);

const SalesOverview = ({ data, loading, currency, setCurrency, oldCurrency }) => {
  const { organisation: { currencies } } = useSelector((state) => state);
  const { enums } = useSelector((state) => state.enums);
  const canvasRef = useRef(null);
  const canvasID = useId();
  const domainConfig = domain();

  useEffect(() => {
    if (loading === false) {
      const canvas = canvasRef.current;
      if (canvas === null) return;

      const ctx = canvas.getContext('2d');
      const colors = domainConfig.isAcquirerFlow ? chartColors.blue : chartColors.purple;
      const gradient = createGradient(ctx, colors.gradientStart, colors.gradientEnd);

      var config = {
        type: "line",
        data: {
          labels: data.map((item) => new Date(item.item_date)),
          dataUnit: "GBP",
          datasets: [
            {
              label: "Payments",
              fill: true,
              backgroundColor: gradient,
              borderColor: colors.primary,
              borderWidth: 2.5,
              tension: 0.4,
              pointRadius: 0,
              pointHoverRadius: 7,
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: colors.primary,
              pointHoverBorderWidth: 3,
              pointHitRadius: 20,
              data: data.map((item) => convertPenniesToDecimals(item.item_total)),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          hover: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              ...richTooltipConfig,
              callbacks: {
                label: function(context) {
                  const value = context.parsed.y;
                  return ` ${CurrencyFormat(enums.currency, currency).format(value)}`;
                },
              },
            },
          },
          animation: richAnimationConfig,
          scales: {
            y: {
              display: true,
              grid: {
                display: true,
                color: 'rgba(148, 163, 184, 0.08)',
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#94a3b8',
                font: {
                  size: 11,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
                padding: 12,
                maxTicksLimit: 5,
                callback: function (value) {
                  return `£${millify(value)}`;
                },
              },
            },
            x: {
              display: true,
              type: 'time',
              time: {
                parser: 'MM/DD/YYYY HH:mm',
                tooltipFormat: 'll',
                unit: 'day',
                unitStepSize: 1,
                displayFormats: {
                  'day': 'Do MMM'
                }
              },
              grid: {
                display: false,
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#94a3b8',
                font: {
                  size: 10,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
                padding: 8,
                maxTicksLimit: 6,
                maxRotation: 0
              },
            },
          },
        },
      };

      const chart = new Chart(canvas, config);

      return () => {
        chart.destroy()
      }
    }
  }, [loading, data, currency, enums.currency, domainConfig.isAcquirerFlow]);

  if (loading || currency !== oldCurrency) {
    return (
      <div className="flex min-h-[400px] flex-col rounded-md border animate-in fade-in-50">
        <CardHeader className="bg-gray-50 mb-2">
          <h2 className={`text-xl ${domainConfig.styles.textColour}`}>
            Payments <span className="font-bold">Overview</span>
          </h2>
        </CardHeader>

        <CardBody className="card-inner pt-0">
          <div className="text-center mb-1 fs-20px w-25 float-right">
            <Skeleton count={1} height="50px" />
          </div>
          <div className="text-center mb-1 fs-20px w-25">
            <Skeleton count={1} height="50px" />
          </div>
          <div className="text-center fs-20px w-100">
            <Skeleton count={1} height="200px" />
          </div>
        </CardBody>
        {currencies.length > 1 && (
          <CardFooter className="bg-light">
            {currencies.map((currencyItem, index) => (
              <Button
                className={`float-right ml-2 ${currencyItem === currency ? 'bg-slate-800' : domainConfig.styles.buttonColour}`}
                onClick={() => setCurrency(currencyItem)}
                key={index}
              >
                {enums.currency[currencyItem].major_unit}
              </Button>
            ))}
          </CardFooter>
        )}
      </div>
    );
  }


  const total = convertPenniesToDecimals(data.reduce((total, item) => total + item.item_total, 0));
  const count = data.reduce((total, item) => total + item.item_count, 0);


  return (
    <div className="flex min-h-[400px] flex-col rounded-md border animate-in fade-in-50">
       <CardHeader className="bg-gray-50 mb-2 text-purple-900">
          <div className="float-right text-sm pt-0.5">
          <span className={`inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-md text-xs font-medium border ${domainConfig.styles.borderColour} ${domainConfig.styles.lightBGColour} ${domainConfig.styles.textColour}`}>Last 30 Days</span>
          </div>
          <h2 className={`text-xl ${domainConfig.styles.textColour}`}>
            Payments <span className="font-bold">Overview</span>
          </h2>
        </CardHeader>

      <CardBody className="card-inner pt-0">
        <div className="nk-sale-data-group align-center justify-between gy-3 gx-5">
          <div className={`nk-sale-data ${domainConfig.styles.textColour}`}>
            <span className={`amount ${domainConfig.styles.textColour}`}>{CurrencyFormat(enums.currency, currency).format(total)}</span>
          </div>
          <div className={`nk-sale-data ${domainConfig.styles.textColour}`}>
            <span className="amount sm">
              {new Intl.NumberFormat('en-GB').format(count)} <small>Completed</small>
            </span>
          </div>
        </div>
        <div className="pt-4 grow max-h-[250px]">
          <canvas id={canvasID} ref={canvasRef}></canvas>
        </div>
      </CardBody>
      {currencies.length > 1 && (
        <CardFooter className="bg-light">
          {currencies.map((currencyItem, index) => (
            <Button
              className={`float-right ml-2 ${currencyItem === currency ? 'bg-slate-800' : domainConfig.styles.buttonColour}`}
              onClick={() => setCurrency(currencyItem)}
              key={index}
            >
              {enums.currency[currencyItem].major_unit}
            </Button>
          ))}
        </CardFooter>
      )}
    </div >
  );
};
export default SalesOverview;
